/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計シートから展開する子票 17 種の構成 SSOT。Backlog の課題種別 ID・件名ラベル・群分け・既定の担当ロール・ラベルの色トーンを 1 箇所に置き、シート（画面）と展開 API が同じ定義を読む
 * @graph-connects none
 */
/** 群の一覧（メニューに出す順）。 @graph-connects none */
export const CHILD_TICKET_GROUPS = [
    {
        id: 'dev',
        labelKey: 'childTicketPlan.groups.dev',
        tone: 'dev',
    },
    {
        id: 'integrationTest',
        labelKey: 'childTicketPlan.groups.integrationTest',
        tone: 'it',
    },
    {
        id: 'releaseTest',
        labelKey: 'childTicketPlan.groups.releaseTest',
        tone: 'uat',
    },
];
/** 子票種別の一覧（群ごとに、メニューに出す順）。 @graph-connects none */
export const CHILD_TICKET_KINDS = [
    // 開発群
    {
        id: 'basic-design',
        label: 'Basic Design',
        group: 'dev',
        tone: 'dev-light',
        issueTypeId: 637531,
        summaryLabel: '基本設計_Basic Design',
        assigneeRole: 'devLeader',
    },
    {
        id: 'basic-design-review',
        label: 'Basic Design Review',
        group: 'dev',
        tone: 'dev-light',
        issueTypeId: 987700,
        summaryLabel: '基本設計レビュー',
        assigneeRole: 'dle',
    },
    {
        id: 'detailed-design',
        label: 'Detailed Design',
        group: 'dev',
        tone: 'dev-mid',
        issueTypeId: 806291,
        summaryLabel: '詳細設計作成',
        assigneeRole: 'devLeader',
    },
    {
        id: 'detailed-design-review',
        label: 'Detailed Design Review',
        group: 'dev',
        tone: 'dev-mid',
        issueTypeId: 753155,
        summaryLabel: '詳細設計レビュー',
        assigneeRole: 'dle',
    },
    {
        id: 'develop',
        label: 'Develop',
        group: 'dev',
        tone: 'dev-deep',
        issueTypeId: 244067,
        summaryLabel: '開発',
        assigneeRole: 'devLeader',
    },
    {
        id: 'code-review',
        label: 'Code Review',
        group: 'dev',
        tone: 'dev-deep',
        issueTypeId: 637530,
        summaryLabel: 'レビュー',
    },
    {
        id: 'dev-buffer',
        label: 'Dev Buffer',
        group: 'dev',
        tone: 'buffer',
        issueTypeId: 684143,
        summaryLabel: '開発バッファ',
    },
    // 結合テスト群
    {
        id: 'it-creation',
        label: 'IT Creation',
        group: 'integrationTest',
        tone: 'it-light',
        issueTypeId: 702065,
        summaryLabel: '結合テスト作成',
    },
    {
        id: 'it-creation-review',
        label: 'IT Creation Review',
        group: 'integrationTest',
        tone: 'it-light',
        issueTypeId: 805716,
        summaryLabel: '結合テスト作成レビュー',
    },
    {
        id: 'it-execution',
        label: 'IT Execution',
        group: 'integrationTest',
        tone: 'it-deep',
        issueTypeId: 637533,
        summaryLabel: '結合テスト実施',
    },
    {
        id: 'it-buffer',
        label: 'IT Buffer',
        group: 'integrationTest',
        tone: 'buffer',
        issueTypeId: 684142,
        summaryLabel: '結合バッファ',
    },
    {
        id: 'release-procedure-creation',
        label: 'Release Procedure Creation',
        group: 'integrationTest',
        tone: 'release-plan',
        issueTypeId: 971148,
        summaryLabel: 'リリース手順書作成',
        assigneeRole: 'devLeader',
    },
    {
        id: 'release-procedure-review',
        label: 'Release Procedure Review',
        group: 'integrationTest',
        tone: 'release-plan',
        issueTypeId: 971149,
        summaryLabel: 'リリース手順書レビュー',
    },
    // 総合テスト・リリース群
    {
        id: 'uat-execution',
        label: 'UAT Execution',
        group: 'releaseTest',
        tone: 'uat',
        issueTypeId: 637538,
        summaryLabel: '総合テスト実施',
        assigneeRole: 'pl',
    },
    {
        id: 'uat-buffer',
        label: 'UAT Buffer',
        group: 'releaseTest',
        tone: 'buffer',
        issueTypeId: 684141,
        summaryLabel: '総合バッファ',
    },
    {
        id: 'release',
        label: 'Release',
        group: 'releaseTest',
        tone: 'release',
        issueTypeId: 637543,
        summaryLabel: 'リリース',
    },
    {
        id: 'app-release',
        label: 'App Release',
        group: 'releaseTest',
        tone: 'release',
        issueTypeId: 821397,
        summaryLabel: 'アプリリリース',
    },
];
/** id → 種別の索引。 @graph-connects none */
const KIND_BY_ID = new Map(CHILD_TICKET_KINDS.map((kind) => [kind.id, kind]));
/**
 * id から種別を引く。未知の id では undefined を返す（行データが古い種別を持っていても落ちない）。
 * @graph-connects none
 */
export function findChildTicketKind(id) {
    return KIND_BY_ID.get(id);
}
/** ラベル（Backlog の子票種別名）→ 種別の索引。照合は大小を無視する。 @graph-connects none */
const KIND_BY_LABEL = new Map(CHILD_TICKET_KINDS.map((kind) => [kind.label.toLowerCase(), kind]));
/**
 * Backlog の子票種別名から種別を引く。表へ貼り付けられた文字列を種別に戻すための唯一の経路で、
 * 一覧に無い名前は undefined を返す（勝手な種別が増えない）。
 * @graph-connects none
 */
export function findChildTicketKindByLabel(label) {
    return KIND_BY_LABEL.get(label.trim().toLowerCase());
}
/**
 * 群に属する種別を、定義順のまま取り出す。
 * @graph-connects none
 */
export function childTicketKindsOf(group) {
    return CHILD_TICKET_KINDS.filter((kind) => kind.group === group);
}
/**
 * その種別に既定で入れる担当者を返す。ロールを持たない種別と、引けなかった人は null。
 *
 * null を別の人で埋めない。決まっていない担当を暫定で埋めると、後からアサインしても
 * その名義が残って別人の実績pt に化ける。
 * @graph-connects none
 */
export function defaultAssigneeFor(kind, people) {
    switch (kind.assigneeRole) {
        case 'devLeader':
            return people.devLeader;
        case 'dle':
            return people.dle;
        case 'pl':
            return people.pl;
        default:
            return null;
    }
}
/** 課題種別 ID → 種別の索引。 @graph-connects none */
const KIND_BY_ISSUE_TYPE_ID = new Map(CHILD_TICKET_KINDS.map((kind) => [kind.issueTypeId, kind]));
/**
 * Backlog の課題種別 ID から種別を引く。定義に無い種別は undefined。
 *
 * 再展開の差分で、既にある子票がシートのどの種別だったかを戻すのに使う。
 * @graph-connects none
 */
export function findChildTicketKindByIssueTypeId(issueTypeId) {
    return KIND_BY_ISSUE_TYPE_ID.get(issueTypeId);
}
/**
 * Backlog の件名に入れられる最大文字数。
 *
 * 超えた件名を送ると Backlog が 400 を返して子票が 1 枚も作れないため、組み立てる側で落とす。
 * @graph-connects none
 */
export const BACKLOG_SUMMARY_MAX_LENGTH = 255;
/**
 * 件名に連ねる 1 片を整える。改行とタブを 1 つの空白へ畳み、前後の空白を落とす。
 *
 * 内容は複数行で書けるので、そのまま件名へ入れると Backlog の一覧で改行が混ざった件名になる。
 * @graph-connects none
 */
function summaryPart(value) {
    return (value ?? '').replace(/\s+/g, ' ').trim();
}
/**
 * 子票の件名（`【ラベル】案件名-項目-リポジトリ-内容`）を組み立てる。
 *
 * 空の欄は区切りごと落とす（項目とリポジトリが無ければ `【ラベル】案件名-内容`）。
 * 並べたときに何の票か件名だけで分かるようにするため、シートの欄をそのまま後ろへ連ねる。
 * `【ラベル】` は人が Backlog 上で作った同種の子票と形を揃えるために残す。
 * @graph-connects none
 */
export function basicDesignChildSummary(kind, projectName, parts) {
    const body = [
        summaryPart(projectName),
        summaryPart(parts?.item ?? null),
        summaryPart(parts?.repository ?? null),
        summaryPart(parts?.content ?? null),
    ]
        .filter((part) => part !== '')
        .join('-');
    const summary = `【${kind.summaryLabel}】${body}`;
    if (summary.length <= BACKLOG_SUMMARY_MAX_LENGTH)
        return summary;
    // 末尾（= 内容の後ろ）から落とす。先頭のラベルと案件名はどの票か特定するのに要る
    return `${summary.slice(0, BACKLOG_SUMMARY_MAX_LENGTH - 1)}…`;
}
/**
 * トーンに対応する CSS 変数名を返す。生のパレット色を書かずにラベル色を解決するための唯一の経路。
 * @graph-connects none
 */
export function childTicketToneVar(tone) {
    return `--color-child-ticket-${tone}`;
}
//# sourceMappingURL=basic-design-kinds.js.map